import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { formatFullName } from "@/lib/aia.queries";
import { boardRosterQueryOptions } from "@/lib/board-snippet.queries";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
/* DISABLED 2026-09-01 per decision d5 (board cards are display-only; profile
   dialog removed). Restore by uncommenting every block marked "DISABLED
   2026-09-01 per decision d5" in this file — imports, DEMO_CONTACTS, the
   `selected` state, the card interaction props, the "View profile" span, the
   <MemberProfileDialog> element, and the MemberProfileDialog component.
import { type AiaMember } from "@/lib/aia.queries";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Globe, Mail, MapPin, Phone } from "lucide-react";
*/
import { Linkedin } from "lucide-react";
import { Suspense, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { PageHero } from "@/components/site/PageHero";
import { noWidow } from "@/lib/utils";

/* DISABLED 2026-09-01 per decision d5 — prototype contact details for the
   profile dialog (only Abrouk for now). Once the layout is approved these move
   to real columns on aia_advisory_members alongside bio.
const DEMO_CONTACTS: Record<
  string,
  { practice?: string; location?: string; phone?: string; email?: string; website?: string }
> = {
  "Michael Abrouk": {
    practice: "California Dermatology and Mohs Surgery Specialists",
    location: "Pasadena, CA",
    phone: "818-654-4791",
    website: "https://www.californiadermatology.com",
  },
  "Lisa Alviti": {
    practice: "Rejuran USA",
    location: "New York, NY",
    email: "marketing@rejuranusa.com",
    website: "https://rejuranusa.com",
  },
};
*/

export const Route = createFileRoute("/board")({
  head: () => ({
    meta: [
      { title: "AIA Industry Advisory Board — Medical Insight" },
      {
        name: "description",
        content:
          "Meet the AIA Industry Advisory Board: executives, physicians, and operators shaping the Aesthetic Industry Association.",
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(boardRosterQueryOptions());
  },
  component: BoardPage,
});

function BoardPage() {
  return (
    <Suspense fallback={<div className="mx-auto max-w-7xl px-4 py-16">Loading…</div>}>
      <AiaBoardSection />
    </Suspense>
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/* ============================================================
   AIA Industry Advisory Board — large searchable roster
   Sourced from miinews.com / industry-association
   ============================================================ */
function AiaBoardSection() {
  const { data: members } = useSuspenseQuery(boardRosterQueryOptions());
  const [q, setQ] = useState("");
  const visible = useMemo(
    () =>
      [...members]
        .filter((m) => m.is_active)
        .sort(
          (a, b) =>
            a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name),
        ),
    [members],
  );
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return visible;
    return visible.filter((m) =>
      [formatFullName(m), m.title, m.org].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [visible, q]);
  /* DISABLED 2026-09-01 per decision d5 (no profile dialog):
  const [selected, setSelected] = useState<AiaMember | null>(null);
  */

  return (
    <div className="bg-background">
      <PageHero
        page="board"
        kicker="Aesthetic Industry Association"
        title="AIA Industry Advisory Board"
        subtitle={"Industry executives, physicians, and operators advising the Aesthetic Industry\u00A0Association."}
        aside={
          <Input
            placeholder="Search by name, title, or company…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-white/95"
          />
        }
      />

      <div className="bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((m) => {
              /* DISABLED 2026-09-01 per decision d5 (cards are display-only —
                 clicking a member no longer opens the profile dialog). Restore
                 by uncommenting this block and putting the commented props back
                 on the card <div> below:
              const hasProfile = Boolean(m.bio);
                  role={hasProfile ? "button" : undefined}
                  tabIndex={hasProfile ? 0 : undefined}
                  onClick={hasProfile ? () => setSelected(m) : undefined}
                  onKeyDown={
                    hasProfile
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelected(m);
                          }
                        }
                      : undefined
                  }
                  className={`flex gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-sm ${
                    hasProfile
                      ? "cursor-pointer hover:ring-1 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      : ""
                  }`}
              */
              return (
                <div
                  key={m.id}
                  className="flex gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-sm"
                >
                  <Avatar className="h-14 w-14 shrink-0">
                    {m.photo_url ? <AvatarImage src={m.photo_url} alt={formatFullName(m)} /> : null}
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {initials(`${m.first_name} ${m.last_name}`)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold leading-tight">{formatFullName(m)}</div>
                    {m.title && (
                      <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                        {noWidow(m.title)}
                      </div>
                    )}
                    {m.org && (
                      <div className="mt-0.5 truncate text-xs font-medium text-foreground/80">
                        {m.org}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-3">
                      {m.linkedin_url && (
                        <a
                          href={m.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                        >
                          <Linkedin className="h-3 w-3" /> LinkedIn
                        </a>
                      )}
                      {/* DISABLED 2026-09-01 per decision d5 (no profile dialog):
                      {hasProfile && (
                        <span className="text-[11px] font-medium text-primary">View profile</span>
                      )}
                      */}
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-sm text-muted-foreground">
                No advisors match your search.
              </div>
            )}
          </div>
          <p className="mt-8 text-xs italic text-muted-foreground">
            *Invited Industry Advisory Board members, subject to change.
          </p>
        </div>
      </div>

      {/* DISABLED 2026-09-01 per decision d5 (no profile dialog):
      <MemberProfileDialog member={selected} onClose={() => setSelected(null)} />
      */}
    </div>
  );
}

/* DISABLED 2026-09-01 per decision d5 (board cards are display-only; the
   member profile dialog is switched off, not deleted). To restore, uncomment
   this whole component plus the marked blocks above (imports, DEMO_CONTACTS,
   `selected` state, card props, "View profile" span, and the
   <MemberProfileDialog> element in AiaBoardSection).
function MemberProfileDialog({
  member,
  onClose,
}: {
  member: AiaMember | null;
  onClose: () => void;
}) {
  const contact = member ? DEMO_CONTACTS[`${member.first_name} ${member.last_name}`] : undefined;
  return (
    <Dialog open={member !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        {member && (
          <>
            <DialogHeader className="text-left">
              <div className="flex items-start gap-4">
                <Avatar className="h-20 w-20 shrink-0 rounded-lg">
                  {member.photo_url ? (
                    <AvatarImage
                      src={member.photo_url}
                      alt={formatFullName(member)}
                      className="rounded-lg object-cover"
                    />
                  ) : null}
                  <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-lg">
                    {initials(`${member.first_name} ${member.last_name}`)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 pt-1">
                  <DialogTitle className="text-xl leading-tight">
                    {formatFullName(member)}
                  </DialogTitle>
                  <DialogDescription asChild>
                    <div className="mt-1">
                      {member.title && (
                        <div className="text-sm text-muted-foreground">{member.title}</div>
                      )}
                      {member.org && (
                        <div className="mt-0.5 text-sm font-medium text-foreground/80">
                          {member.org}
                        </div>
                      )}
                    </div>
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {member.bio && (
              <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
                {member.bio.split(/\n\n+/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}

            {(contact || member.linkedin_url) && (
              <div className="rounded-lg border bg-secondary/40 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Contact
                </div>
                <div className="mt-2 space-y-1.5 text-sm">
                  {contact?.practice && <div className="font-medium">{contact.practice}</div>}
                  {contact?.location && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" /> {contact.location}
                    </div>
                  )}
                  {contact?.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      <a href={`tel:${contact.phone}`} className="hover:underline">
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  {contact?.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                        {contact.email}
                      </a>
                    </div>
                  )}
                  {contact?.website && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-3.5 w-3.5 shrink-0" />
                      <a
                        href={contact.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {contact.website.replace(/^https?:\/\/(www\.)?/, "")}
                      </a>
                    </div>
                  )}
                  {member.linkedin_url && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Linkedin className="h-3.5 w-3.5 shrink-0" />
                      <a
                        href={member.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        LinkedIn profile
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
*/
